import { apiClient } from './client';
import type {
  AddAdresseInput,
  AddMoyenPaiementInput,
  Adresse,
  Client,
  ClientListItem,
  CreateClientInput,
  CreateClientPayload,
  ListClientsParams,
  MoyenPaiement,
  Paginated,
  UpdateClientInput,
} from './types';

/**
 * Endpoints `/api/v1/clients/` — Phase 2.
 *
 * Le géocodage automatique (lat/lon) est délégué au backend en Phase 5 ; la
 * console Admin saisit pour l'instant les coordonnées manuellement.
 */

/**
 * Le `ClientViewSet` n'expose que `?type=` (filterset_fields) et `?search=`
 * (search_fields : raison_sociale, numero_contribuable, telephone_principal,
 * user__email). Il n'y a PAS de filtre `ville` côté backend — il est donc
 * appliqué côté client (cf. ListPage). On ne l'envoie pas à l'API.
 */
function cleanParams(params: ListClientsParams): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (params.page !== undefined) out.page = params.page;
  if (params.page_size !== undefined) out.page_size = params.page_size;
  if (params.type !== undefined) out.type = params.type;
  if (params.search !== undefined && params.search.length > 0) out.search = params.search;
  return out;
}

export async function listClients(
  params: ListClientsParams = {},
): Promise<Paginated<ClientListItem>> {
  const { data } = await apiClient.get<Paginated<ClientListItem>>('/clients/', {
    params: cleanParams(params),
  });
  return data;
}

export async function getClient(id: string): Promise<Client> {
  const { data } = await apiClient.get<Client>(`/clients/${id}/`);
  return data;
}

/**
 * Construit la charge utile exacte attendue par `POST /clients/`.
 *
 * Le serializer ne crée que le `Client` + son compte `User` ; l'adresse, la
 * géolocalisation et les moyens de paiement sont des relations en lecture seule
 * à la création et se renseignent ensuite via leurs endpoints dédiés. Pour un
 * FOYER, `nom`/`prenom` alimentent `last_name`/`first_name` ; pour une
 * ENTREPRISE, c'est `raison_sociale` qui porte l'identité.
 */
export function toCreateClientPayload(input: CreateClientInput): CreateClientPayload {
  const payload: CreateClientPayload = {
    type: input.type,
    telephone_principal: input.telephone.trim(),
  };
  const email = input.email?.trim();
  if (email !== undefined && email.length > 0) {
    payload.email = email;
  }
  if (input.type === 'FOYER') {
    const lastName = input.nom?.trim();
    const firstName = input.prenom?.trim();
    if (lastName !== undefined && lastName.length > 0) payload.last_name = lastName;
    if (firstName !== undefined && firstName.length > 0) payload.first_name = firstName;
  } else {
    const raison = input.raison_sociale?.trim();
    if (raison !== undefined && raison.length > 0) payload.raison_sociale = raison;
  }
  const contribuable = input.numero_contribuable?.trim();
  if (contribuable !== undefined && contribuable.length > 0) {
    payload.numero_contribuable = contribuable;
  }
  return payload;
}

export async function createClient(input: CreateClientInput): Promise<Client> {
  const { data } = await apiClient.post<Client>('/clients/', toCreateClientPayload(input));
  return data;
}

/**
 * Construit la charge utile d'un `PATCH /clients/{id}/` (édition des infos).
 *
 * On n'envoie que les champs pertinents au type : prénom/nom pour un FOYER,
 * raison sociale + n° contribuable pour une ENTREPRISE. L'e-mail vide est omis
 * (le serializer DRF refuse une chaîne vide) ; les champs identité vides sont
 * envoyés pour permettre l'effacement.
 */
export function toUpdateClientPayload(input: UpdateClientInput): Record<string, unknown> {
  const payload: Record<string, unknown> = { type: input.type };
  const email = input.email?.trim();
  if (email !== undefined && email.length > 0) payload.email = email;
  const tel = input.telephone_principal?.trim();
  if (tel !== undefined) payload.telephone_principal = tel;
  if (input.type === 'FOYER') {
    payload.first_name = input.first_name?.trim() ?? '';
    payload.last_name = input.last_name?.trim() ?? '';
  } else {
    payload.raison_sociale = input.raison_sociale?.trim() ?? '';
    payload.numero_contribuable = input.numero_contribuable?.trim() ?? '';
  }
  return payload;
}

export async function updateClient(id: string, input: UpdateClientInput): Promise<Client> {
  const { data } = await apiClient.patch<Client>(`/clients/${id}/`, toUpdateClientPayload(input));
  return data;
}

/**
 * Charge utile exacte attendue par `POST /clients/{id}/adresses/`
 * (`AdresseSerializer`). Le modèle n'a PAS de `ligne1` : on mappe la « ligne 1 »
 * du formulaire vers `repere` (concaténée avec un éventuel repère saisi). Les
 * coordonnées (DecimalField) sont envoyées en chaîne décimale ou omises.
 */
export interface AddAdressePayload {
  ville: string;
  quartier?: string;
  repere?: string;
  latitude?: string;
  longitude?: string;
}

/** Sérialise une coordonnée numérique pour DRF (DecimalField), ou undefined. */
function serializeCoord(v: number | undefined): string | undefined {
  if (v === undefined || !Number.isFinite(v)) return undefined;
  // Le backend (DecimalField max_digits=9, decimal_places=6) rejette plus de
  // 6 décimales. La carte/géolocalisation renvoie souvent 7 à 10 décimales —
  // on arrondit à 6 (≈ 0,11 m, largement suffisant pour la géofence 50 m).
  return v.toFixed(6);
}

/**
 * Mappe la saisie d'adresse de la console vers la charge utile du serializer.
 * `ligne1` (libellé « adresse ligne 1 ») et `repere` sont fusionnés dans le
 * champ `repere` réel du modèle. Les chaînes vides sont omises.
 */
export function toAddAdressePayload(input: AddAdresseInput): AddAdressePayload {
  const payload: AddAdressePayload = { ville: input.ville.trim() };

  const quartier = input.quartier?.trim();
  if (quartier !== undefined && quartier.length > 0) payload.quartier = quartier;

  const ligne1 = input.ligne1?.trim() ?? '';
  const repere = input.repere?.trim() ?? '';
  const repereParts = [ligne1, repere].filter((p) => p.length > 0);
  if (repereParts.length > 0) payload.repere = repereParts.join(' — ');

  const latitude = serializeCoord(input.latitude);
  if (latitude !== undefined) payload.latitude = latitude;
  const longitude = serializeCoord(input.longitude);
  if (longitude !== undefined) payload.longitude = longitude;

  return payload;
}

export async function addAdresseClient(
  clientId: string,
  input: AddAdresseInput,
): Promise<Adresse> {
  const { data } = await apiClient.post<Adresse>(
    `/clients/${clientId}/adresses/`,
    toAddAdressePayload(input),
  );
  return data;
}

export async function listAdressesClient(clientId: string): Promise<Adresse[]> {
  const { data } = await apiClient.get<Paginated<Adresse> | Adresse[]>(
    `/clients/${clientId}/adresses/`,
  );
  return Array.isArray(data) ? data : data.results;
}

export async function addMoyenPaiement(
  clientId: string,
  payload: AddMoyenPaiementInput,
): Promise<MoyenPaiement> {
  const { data } = await apiClient.post<MoyenPaiement>(
    `/clients/${clientId}/moyens-paiement/`,
    payload,
  );
  return data;
}

export async function removeMoyenPaiement(clientId: string, moyenId: string): Promise<void> {
  await apiClient.delete<void>(`/clients/${clientId}/moyens-paiement/${moyenId}/`);
}

/**
 * Définit / réinitialise le mot de passe de connexion d'un client (admin).
 * Le client se connecte par e-mail + mot de passe ; le drapeau « à changer à
 * la connexion » est levé côté serveur.
 */
export async function definirMotDePasseClient(id: string, password: string): Promise<void> {
  await apiClient.post(`/clients/${id}/definir-mot-de-passe/`, { password });
}
